import React from 'react';
import { Database, Loader2 } from 'lucide-react';
import { Radio } from '../../ui/Radio';

interface SchemaEvaluationModeCardProps {
  schemaMode: 'dynamic' | 'strict';
  isSavingSchemaMode: boolean;
  canEdit: boolean;
  onSaveSchemaMode: (mode: 'dynamic' | 'strict') => void;
}

export const SchemaEvaluationModeCard: React.FC<SchemaEvaluationModeCardProps> = ({
  schemaMode,
  isSavingSchemaMode,
  canEdit,
  onSaveSchemaMode,
}) => {
  return (
    <div className="p-4 rounded-lg bg-cf-surface border border-cf-border space-y-3 font-sans text-xs">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-cf-textPrimary text-xs uppercase tracking-wider flex items-center gap-1.5">
          <Database size={13} className="text-cf-orange" />
          <span>Schema Evaluation Mode</span>
        </h3>
        {isSavingSchemaMode && (
          <span className="flex items-center gap-1.5 text-xs text-cf-orange font-medium animate-pulse">
            <Loader2 size={12} className="animate-spin" />
            <span>Updating mode...</span>
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div
          onClick={() => (!canEdit || isSavingSchemaMode ? null : onSaveSchemaMode('dynamic'))}
          className={`p-3 rounded border bg-cf-bg cursor-pointer transition-colors ${
            schemaMode === 'dynamic'
              ? 'border-cf-orange text-cf-textPrimary shadow-xs'
              : 'border-cf-border text-cf-textSecondary hover:text-cf-textPrimary'
          }`}
        >
          <Radio
            name="schemaMode"
            value="dynamic"
            checked={schemaMode === 'dynamic'}
            onChange={() => onSaveSchemaMode('dynamic')}
            disabled={!canEdit || isSavingSchemaMode}
            label="Dynamic Discovery (Recommended)"
            description="Automatically discovers and creates columns for any new field in incoming form data or webhook JSON payloads."
          />
        </div>

        <div
          onClick={() => (!canEdit || isSavingSchemaMode ? null : onSaveSchemaMode('strict'))}
          className={`p-3 rounded border bg-cf-bg cursor-pointer transition-colors ${
            schemaMode === 'strict'
              ? 'border-cf-orange text-cf-textPrimary shadow-xs'
              : 'border-cf-border text-cf-textSecondary hover:text-cf-textPrimary'
          }`}
        >
          <Radio
            name="schemaMode"
            value="strict"
            checked={schemaMode === 'strict'}
            onChange={() => onSaveSchemaMode('strict')}
            disabled={!canEdit || isSavingSchemaMode}
            label="Strict Schema Validation"
            description="Rejects any submission payload containing unmapped fields or invalid types. Enforces pre-defined schema strictly."
          />
        </div>
      </div>
    </div>
  );
};
